import type { QualityFlag, QualitySeverity } from "@/lib/api";
import { cn } from "@/lib/utils";

interface QualityFlagsPillProps {
  flags: QualityFlag[] | undefined | null;
  /** Additional Tailwind classes (spacing, margin) applied at the call
   *  site. The pill already provides its own base layout, so callers
   *  rarely need to pass anything here. */
  className?: string;
}

// dominant picks the "worst" severity present on the row. Scanner
// block > warn > nothing. The pill color keys off this so the user
// sees the most-severe concern at a glance.
function dominantSeverity(flags: QualityFlag[]): QualitySeverity | null {
  let hasBlock = false;
  let hasWarn = false;
  for (const f of flags) {
    if (f.severity === "block") hasBlock = true;
    else if (f.severity === "warn") hasWarn = true;
  }
  if (hasBlock) return "block";
  if (hasWarn) return "warn";
  return null;
}

const severityClass: Record<QualitySeverity, string> = {
  block:
    "bg-rose-500/15 text-rose-700 border border-rose-500/30 dark:text-rose-300",
  warn:
    "bg-amber-500/15 text-amber-700 border border-amber-500/30 dark:text-amber-300",
};

/** QualityFlagsPill renders a compact "!" badge for any row that
 *  carries one or more quality-flag annotations. Color keys off the
 *  highest severity present; the native `title` tooltip shows the
 *  message list so a mouse hover reveals why the row was flagged.
 *
 *  Returns null for empty / undefined flag lists so callers can drop
 *  it in unconditionally without a wrapping conditional. */
export function QualityFlagsPill({ flags, className }: QualityFlagsPillProps) {
  if (!flags || flags.length === 0) return null;
  const sev = dominantSeverity(flags);
  if (!sev) return null;
  // Title text: one line per flag, code + message. Native tooltip
  // keeps the implementation dependency-free and accessible.
  const title = flags.map((f) => `${f.code}: ${f.message}`).join("\n");
  return (
    <span
      title={title}
      className={cn(
        "ml-1.5 inline-flex items-center rounded px-1 text-[10px] font-bold leading-4",
        severityClass[sev],
        className,
      )}
      aria-label={`Quality flags: ${flags.map((f) => f.code).join(", ")}`}
    >
      !{flags.length > 1 ? ` ${flags.length}` : ""}
    </span>
  );
}
